/**
 * Checkout Initial Values Helpers
 *
 * Generates initial form values for different checkout types
 * (physical products vs digital/e-learning)
 */

import type {
  CheckoutFormValues,
  AddressData,
  DebtorData,
  CartData,
} from '../types/checkout.types';

/**
 * Parameters needed to generate checkout initial values
 */
export interface CheckoutInitialValuesData {
  customerEmail: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  billingEmail: string;
  billingReference: string;
  billingAddress: AddressData;
  debtor: DebtorData;
  cart: CartData;
  defaultAddressIndex: number | null;
  orderForSomeOneElse?: boolean;
  /**
   * User's saved addresses array.
   * Used to populate delivery fields when defaultAddressIndex is set.
   */
  addresses?: AddressData[];
}

/**
 * Get base checkout values shared between all checkout types
 * Contains personal information and billing details
 */
const getBaseCheckoutValues = (
  data: CheckoutInitialValuesData,
): Pick<
CheckoutFormValues,
| 'business_option'
| 'email'
| 'personal_given_name'
| 'personal_family_name'
| 'invoice_email'
| 'customer_reference'
| 'telephone'
| 'billing_given_name'
| 'billing_family_name'
| 'billing_organization'
| 'billing_locality'
| 'billing_postal_code'
| 'billing_address_line1'
| 'billing_address_line2'
| 'order_someone_else'
> => ({
  business_option: 'business',
  email: data.customerEmail || '',
  personal_given_name: data.firstName,
  personal_family_name: data.lastName,
  invoice_email: data.billingEmail || '',
  // Reference is a per-order field, entered fresh at checkout (no longer
  // persisted on the profile), so it is never pre-filled.
  customer_reference: '',
  telephone: data.phoneNumber,
  billing_given_name: data.billingAddress.given_name || '',
  billing_family_name: data.billingAddress.family_name || '',
  billing_organization:
    data.billingAddress.organization ||
    data.debtor.searchName ||
    data.cart.content?.attributes?.billing_information?.address?.organization ||
    '',
  billing_locality:
    data.billingAddress.locality ||
    data.debtor.city ||
    data.cart.content?.attributes?.billing_information?.address?.locality ||
    '',
  billing_postal_code:
    data.billingAddress.postal_code ||
    data.debtor.zipCode ||
    data.cart.content?.attributes?.billing_information?.address?.postal_code ||
    '',
  billing_address_line1:
    data.billingAddress.address_line1 ||
    data.debtor.addressFormatted?.streetName ||
    data.cart.content?.attributes?.billing_information?.address
      ?.address_line1 ||
    '',
  billing_address_line2:
    data.billingAddress.address_line2 ||
    data.debtor.addressFormatted?.houseNumber ||
    data.cart.content?.attributes?.billing_information?.address
      ?.address_line2 ||
    '',
  order_someone_else: data.orderForSomeOneElse ?? false,
});

/**
 * Get initial values for physical product checkout
 * Includes delivery address and order-for-someone-else options
 *
 * When defaultAddressIndex is set and addresses are provided,
 * delivery fields are pre-populated to ensure form validation passes on first submit.
 */
export const getPhysicalCheckoutInitialValues = (
  data: CheckoutInitialValuesData,
): CheckoutFormValues => {
  // Pre-populate delivery fields from user's default address (if available)
  const defaultAddress =
    data.defaultAddressIndex !== null
      ? data.addresses?.[data.defaultAddressIndex] ?? null
      : null;

  return {
    ...getBaseCheckoutValues(data),
    // Delivery address fields - pre-populate when default address exists
    organization: defaultAddress?.organization ?? '',
    given_name: defaultAddress?.given_name ?? '',
    family_name: defaultAddress?.family_name ?? '',
    locality: defaultAddress?.locality ?? '',
    postal_code: defaultAddress?.postal_code ?? '',
    address_line1: defaultAddress?.address_line1 ?? '',
    address_line2: defaultAddress?.address_line2 ?? '',
    // Order options
    delivery_differs_from_billing: data.defaultAddressIndex !== null,
    set_default: false,
  };
};
